import { useCallback, useEffect, useState } from 'react';
import type { AnalyzerIntegrationStatus, AnalyzerRunLog } from '@ozon-ai/historical/contracts';
import { ANALYZER_STATUS_LABELS } from '@ozon-ai/historical/contracts';
import { Button } from '@/components/ui/button.tsx';
import { api } from '../../../api/client';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { StatusBadge } from '../../../ui/StatusBadge';
import { SETTINGS_DEVELOPMENT } from '../settings-paths';
import {
  SettingsErrorAlert,
  SettingsFormCard,
  SettingsHint,
  SettingsSubPageShell,
} from '../settings-shared';

function parseStringList(json: string): string[] {
  try {
    const value = JSON.parse(json) as unknown;
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function parseIntegrationStatus(json: string | null): AnalyzerIntegrationStatus | undefined {
  if (!json) return undefined;
  try {
    return JSON.parse(json) as AnalyzerIntegrationStatus;
  } catch {
    return undefined;
  }
}

export function SettingsAnalyzerLogsPage() {
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<AnalyzerRunLog[]>([]);

  const load = useCallback(async () => {
    try {
      setError(null);
      setLogsLoading(true);
      const runLogs = await api.get<AnalyzerRunLog[]>('/analyzer/logs?limit=15');
      setLogs(runLogs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить логи');
      setLogs([]);
    } finally {
      setLoading(false);
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingSpinner />;

  return (
    <SettingsSubPageShell title="Логи анализа" backTo={SETTINGS_DEVELOPMENT}>
      {error && <SettingsErrorAlert>{error}</SettingsErrorAlert>}

      <SettingsFormCard>
        <div className="mb-3 flex items-center justify-between gap-2">
          <SettingsHint>Последние 15 запусков</SettingsHint>
          <Button type="button" variant="ghost" size="sm" disabled={logsLoading} onClick={() => void load()}>
            Обновить
          </Button>
        </div>

        {logsLoading && logs.length === 0 ? (
          <LoadingSpinner compact />
        ) : logs.length === 0 ? (
          <SettingsHint>Запусков анализа пока нет.</SettingsHint>
        ) : (
          <ul className="flex flex-col gap-3">
            {logs.map((log) => {
              const errors = parseStringList(log.errorsJson);
              const warnings = parseStringList(log.warningsJson);
              const integrationStatus = parseIntegrationStatus(log.integrationStatusJson);

              return (
              <li key={log.id} className="flex flex-col gap-1 border-b border-border/60 py-2 last:border-0">
                <div className="flex min-w-0 items-center justify-between gap-2">
                  <span className="min-w-0 flex-1 shrink font-bold line-clamp-2">
                    {new Date(log.finishedAt).toLocaleString('ru-RU')}
                  </span>
                  <StatusBadge status={log.status}>
                    {ANALYZER_STATUS_LABELS[log.status] || log.status}
                  </StatusBadge>
                </div>
                <SettingsHint>
                  Офферов: {log.offersAnalyzed} · Действий: {log.actionsCount}
                </SettingsHint>
                {(log.planId || log.snapshotId) && (
                  <SettingsHint>
                    {log.planId ? `План: ${log.planId}` : ''}
                    {log.planId && log.snapshotId ? ' · ' : ''}
                    {log.snapshotId ? `Snapshot: ${log.snapshotId}` : ''}
                  </SettingsHint>
                )}
                {integrationStatus && (
                  <SettingsHint>
                    Ozon: {integrationStatus.ozon.ok ? 'ok' : integrationStatus.ozon.error || 'ошибка'}
                    {' · '}
                    Sellmonitor:{' '}
                    {integrationStatus.sellmonitor.ok
                      ? 'ok'
                      : integrationStatus.sellmonitor.error || 'ошибка'}
                  </SettingsHint>
                )}
                {errors.length > 0 && (
                  <p className="mt-1 text-sm text-destructive">{errors.join(' · ')}</p>
                )}
                {warnings.length > 0 && (
                  <SettingsHint className="mt-1">{warnings.join(' · ')}</SettingsHint>
                )}
              </li>
            );
            })}
          </ul>
        )}
      </SettingsFormCard>
    </SettingsSubPageShell>
  );
}
